import React, { useState, useEffect } from "react";
import { Button, List, Avatar, Card, Space, Modal, Tooltip, Alert, message } from "antd";
import { API } from "../../config/config";
import axios from "axios";
import moment from "moment";
import Cookies from "js-cookie";

import { QuestionCircleOutlined } from "@ant-design/icons";
import AlertCreditDocumentUpload from "./AlertCreditDocumentUpload";
import { FileViewer } from "../../helper/FileViewer";
import CreditDocumentTooltip from "../../helper/CreditDocumentTooltip";
import { isIneligibleCredit, creditIneligibleMessage } from "../../helper/CreditIneligibleReason";

const DATA_URL = "/alert_credit_documents";

const AlertCreditDocuments = (props) => {
    const { t, alertid, isRefresh, callback = () => {} } = props;
    const [documents, setDocuments] = useState([]);
    // null until the credit check answers, so the card doesn't flash on alerts without a credit.
    const [hasCredit, setHasCredit] = useState(null);
    // Latest credit row (the API orders by alertcreditid DESC); drives the ineligible notice.
    const [credit, setCredit] = useState(null);
    const [creditCheckFailed, setCreditCheckFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const headers = { Authorization: "Bearer " + Cookies.get("access_token"), Authuser: Cookies.get("authuser") };

        setIsLoading(true);
        setLoadFailed(false);
        setCreditCheckFailed(false);
        setCredit(null);
        setDocuments([]);

        // Both requests go out together — the credit check is small and reveals the card,
        // while the document list carries the file payloads and takes the longer trip.
        axios
            .post(API + "/alert_credits", { alertid: alertid }, { headers })
            .then(({ data }) => {
                if (cancelled) return;
                const found = Array.isArray(data) && data.length > 0;
                setHasCredit(found);
                setCredit(found ? data[0] : null);
            })
            .catch(() => {
                if (!cancelled) {
                    setHasCredit(false);
                    setCredit(null);
                    setCreditCheckFailed(true);
                }
            });

        axios
            .post(API + DATA_URL, { alertid: alertid }, { headers })
            .then(({ data }) => {
                if (!cancelled && data) setDocuments(data);
            })
            .catch(() => {
                // Without this the list would settle into an empty state, which reads as
                // "no evidence was uploaded" rather than "the request failed".
                if (!cancelled) setLoadFailed(true);
            })
            .then(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [refreshKey, isRefresh, alertid]); // eslint-disable-line react-hooks/exhaustive-deps

    const getIconFile = (type) => {
        switch (type) {
            case "pdf":
                return require(`../../assets/images/files/pdf.svg`);
            case "doc":
                return require(`../../assets/images/files/doc.svg`);
            case "docx":
                return require(`../../assets/images/files/doc.svg`);
            case "png":
                return require(`../../assets/images/files/png.svg`);
            case "jpg":
                return require(`../../assets/images/files/jpg.svg`);
            case "jpeg":
                return require(`../../assets/images/files/jpg.svg`);
            default:
                return require(`../../assets/images/files/unknown.svg`);
        }
    };

    const getMimeType = (type) => {
        switch (type) {
            case "pdf":
                return "application/pdf";
            case "doc":
                return "application/msword";
            case "docx":
                return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            case "jpg":
                return "image/jpg";
            case "jpeg":
                return "image/jpeg";
            case "png":
                return "image/png";
            default:
                return false;
        }
    };

    const viewFile = (item) => {
        const MimeType = getMimeType(item.document_type);
        if (!MimeType) {
            console.log("MIME type not found.");
            return false;
        }
        const linkSource = `data:${MimeType};base64,${item.document}`;
        FileViewer.open(item.document_name, item.document_type, linkSource, t);
    }
    

    const confirmDelete = (item) => {
        Modal.confirm({
            title: "Delete File",
            content: `Are you sure you want to delete "${item.document_name}.${item.document_type}"?`,
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",

            onOk: async () => {
                try {
                    await deleteFile(item.alertcreditdocumentid);
                    message.success("File deleted successfully");
                    setRefreshKey((k) => k + 1)
                    callback();

                } catch (err) {
                    message.error("Failed to delete file");
                }
            },
        });
    };

    const deleteFile = async (alertcreditdocumentid) => {
        try {
            await axios.post(
                API + DATA_URL + "_delete",
                { alertcreditdocumentid },
                {
                    headers: {
                    Authorization: "Bearer " + Cookies.get("access_token"),
                    Authuser: Cookies.get("authuser"),
                    },
                }
            );
        } catch (error) {
            throw new Error(
            error.response?.data?.message || "Delete failed"
            );
        }
    };

    const getList = () => (
        <List
            size="small"
            itemLayout="horizontal"
            dataSource={documents}
            loading={isLoading}
            header={
                <div className="d-flex justify-content-between" style={{ padding: "0 16px" }}>
                    <div>
                        <strong>
                            {t("tables.fields.files")} ({isLoading ? "..." : documents.length})
                        </strong>
                    </div>
                    <div>
                        <strong>{t("tables.fields.action")}</strong>
                    </div>
                </div>
            }
            renderItem={(item) => (
                <List.Item>
                    <List.Item.Meta
                        avatar={<Avatar shape="square" src={getIconFile(item.document_type)} />}
                        title={`${item.document_name}.${item.document_type}`}
                        description={<small>{moment(item.document_upload_date).format("DD/MM/YYYY HH:mm:ss")}</small>}
                    />

                    <Space size={6}>
                        <Button type="primary" ghost shape="round" size="small" onClick={() => viewFile(item)}>
                            {t("tables.actions.view") || "View"}
                        </Button>

                        <Button danger shape="round" size="small" onClick={() => confirmDelete(item)}>
                            {t("tables.actions.delete") || "Delete"}
                        </Button>
                    </Space>
                </List.Item>
            )}
        />
    );

    // A failed credit check means "unknown", not "no credit" — show the card and let the
    // list speak for itself, rather than hiding the upload button on a transient error.
    if (!hasCredit && !creditCheckFailed) return null;

    return (
        <div style={{ marginBottom: "1rem" }}>
            <Card
                key="AlertCreditDocument"
                size="small"
                headStyle={{ fontWeight: 'bold' }}
                title={t("contents.alerts.alert_credit_documents")}
                extra={
                    <Space size={8}>
                        <AlertCreditDocumentUpload
                            t={t}
                            alertid={alertid}
                            existingFilenames={documents.map((d) => `${d.document_name}.${d.document_type}`)}
                            onUploadSuccess={() => {setRefreshKey((k) => k + 1); callback();}}
                        />
                        <Tooltip title={<CreditDocumentTooltip t={t} />} overlayStyle={{ maxWidth: 400 }}>
                            <QuestionCircleOutlined style={{ color: "#999", cursor: "pointer" }} />
                        </Tooltip>
                    </Space>
                }
            >
                {isIneligibleCredit(credit) && (
                    <Alert
                        type="warning"
                        showIcon
                        message={creditIneligibleMessage(t, credit.credit_status_reason)}
                        style={{ marginBottom: 8 }}
                    />
                )}
                {loadFailed ? (
                    <div style={{ padding: "16px", textAlign: "center", color: "#cf1322" }}>
                        {t("contents.alerts.documents_load_failed")}
                    </div>
                ) : (
                    getList()
                )}
            </Card>
        </div>
    );
};
export default AlertCreditDocuments;
